<!--ABOUTME: Privacy Policy page displaying current privacy policy with version information-->
<!--ABOUTME: Static page that shows privacy policy content with proper formatting and navigation-->

<template>
    <div class="policy-page">
        <div class="policy-container">
            <div class="policy-header">
                <h1>Privacy Policy</h1>
                <div class="policy-meta">
                    <span class="version">Version 1.0</span>
                    <span class="separator">•</span>
                    <span class="effective-date"
                        >Effective Date: 08 September 2025</span
                    >
                </div>
            </div>

            <div class="policy-content">
                <div class="provider-info">
                    <p>
                        <strong>Provider:</strong> Stekz B.V., Groningen,
                        Netherlands —
                        <a href="mailto:legal@stekz.com">legal@stekz.com</a>
                    </p>
                </div>

                <p>
                    We process your personal data when you interact with your
                    account on MyStekz. We handle your (personal) data
                    confidentially and comply with legal obligations such as the
                    GDPR. We process your personal data purely to be able to
                    carry out our work, not for any other commercial activity
                    around personal data itself.
                </p>

                <p>
                    We collect only what is minimally necessary for you to
                    create and manage an account:
                </p>
                <ul>
                    <li>Name</li>
                    <li>E-mail address</li>
                </ul>

                <p>
                    In order to use our platform, you provide MyStekz with
                    organizational data such as the name, roles, definitions and
                    processes. MyStekz does not control which information is
                    entered. Therefore, you should not enter data into MyStekz
                    which is restricted under the GDPR, e.g. data from your
                    customers.
                </p>

                <p>
                    We never disclose your personal data to third parties,
                    unless required by a legal requirement or if you yourself
                    have given explicit permission to do so.
                </p>

                <p>
                    When using MyStekz, certain cookies are automatically placed
                    on your device. These cookies are for essential use of
                    MyStekz.
                </p>

                <p>
                    You authorise Stekz to use subprocessors. The list of
                    subprocessors can be found in the DPA in the Terms of Use.
                </p>
            </div>

            <div class="policy-footer">
                <p class="last-updated">Last updated: 08 September 2025</p>
                <div class="contact-info">
                    <p>Questions about this Privacy Policy? Contact us at:</p>
                    <p>
                        <strong
                            ><a href="mailto:legal@stekz.com"
                                >legal@stekz.com</a
                            ></strong
                        >
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
// No reactive data needed for static policy page
// You can add analytics tracking or other functionality here if needed
</script>

<style lang="scss" scoped>
.policy-page {
    min-height: 100vh;
    background-color: var(--surface-ground);
    padding: 2rem 1rem;

    @media (max-width: 768px) {
        padding: 1rem 0.5rem;
    }
}

.policy-container {
    max-width: 800px;
    margin: 0 auto;
    background-color: var(--surface-card);
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.policy-header {
    background-color: var(--primary-color);
    color: var(--primary-color-text);
    padding: 2rem;
    text-align: center;

    h1 {
        margin: 0 0 1rem 0;
        font-size: 2.5rem;
        font-weight: 300;

        @media (max-width: 768px) {
            font-size: 2rem;
        }
    }

    .policy-meta {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.9rem;
        opacity: 0.9;

        @media (max-width: 768px) {
            flex-direction: column;
            gap: 0.25rem;

            .separator {
                display: none;
            }
        }

        .version {
            font-weight: 500;
        }
    }
}

.policy-content {
    padding: 2rem;
    line-height: 1.6;
    color: var(--text-color);

    @media (max-width: 768px) {
        padding: 1.5rem;
    }

    h2 {
        color: var(--primary-color);
        margin: 2rem 0 1rem 0;
        padding-bottom: 0.5rem;
        border-bottom: 2px solid var(--surface-border);
    }

    h3 {
        color: var(--text-color);
        margin: 1.5rem 0 0.5rem 0;
    }

    p {
        margin-bottom: 1rem;
    }

    ul {
        margin: 1rem 0;
        padding-left: 1.5rem;

        li {
            margin-bottom: 0.5rem;
        }
    }
}

.placeholder-content {
    em {
        color: var(--text-color-secondary);
        font-size: 1.1rem;
    }
}

.section-suggestions {
    margin-top: 1.5rem;

    .section-item {
        margin-bottom: 1.5rem;
        padding: 1rem;
        background-color: var(--surface-50);
        border-radius: 6px;
        border-left: 4px solid var(--primary-color);

        h3 {
            margin: 0 0 0.5rem 0;
            color: var(--primary-color);
            font-size: 1.1rem;
        }

        .section-description {
            margin: 0;
            color: var(--text-color-secondary);
            font-size: 0.9rem;
        }
    }
}

.policy-footer {
    background-color: var(--surface-50);
    padding: 2rem;
    border-top: 1px solid var(--surface-border);
    text-align: center;

    @media (max-width: 768px) {
        padding: 1.5rem;
    }

    .last-updated {
        margin: 0 0 1rem 0;
        font-size: 0.9rem;
        color: var(--text-color-secondary);
    }

    .contact-info {
        p {
            margin: 0.5rem 0;

            &:first-child {
                color: var(--text-color-secondary);
                font-size: 0.9rem;
            }

            strong {
                color: var(--primary-color);
            }
        }
    }
}
</style>
